import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { company, navLinks } from "../mock/mock";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMobileSubOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const isChildActive = (children) => children.some((c) => isActive(c.path));

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md py-2" : "bg-white/95 py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" className="logo-link flex items-center gap-3 shrink-0">
          <img
            src={company.logo}
            alt={company.name}
            className="logo-anim logo-float w-auto object-contain"
            style={{ height: scrolled ? "46px" : "58px", transition: "height 0.3s ease" }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.children ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-semibold transition-colors duration-200 ${
                    isChildActive(link.children) ? "text-[#1B2A4A]" : "text-gray-600 hover:text-[#1B2A4A]"
                  }`}
                >
                  {link.label}
                  <ChevronDown size={15} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
                {servicesOpen && (
                  <div className="absolute left-0 top-full pt-2 w-72 animate-fadeIn">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block px-4 py-3 rounded-lg transition-colors ${
                            isActive(child.path) ? "bg-[#1B2A4A]/5" : "hover:bg-gray-50"
                          }`}
                        >
                          <span className="block font-semibold text-sm text-[#1B2A4A]">{child.label}</span>
                          <span className="block text-xs text-gray-500 mt-0.5">{child.desc}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors duration-200 ${
                  isActive(link.path) ? "text-[#1B2A4A]" : "text-gray-600 hover:text-[#1B2A4A]"
                }`}
              >
                {link.label}
                {isActive(link.path) && <span className="block h-0.5 bg-[#C9A227] rounded-full mt-0.5" />}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <a
            href={`tel:${company.phone.replace(/\s/g, "")}`}
            className="flex items-center gap-2 text-sm font-semibold text-[#1B2A4A]"
          >
            <Phone size={16} className="text-[#C9A227]" />
            {company.phone}
          </a>
          <button
            onClick={() => navigate("/contact")}
            className="bg-[#1B2A4A] hover:bg-[#26406b] text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors duration-200 shadow-sm"
          >
            Devis gratuit
          </button>
        </div>

        <button
          className="lg:hidden text-[#1B2A4A] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg animate-fadeIn max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <button
                    onClick={() => setMobileSubOpen(!mobileSubOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-md text-base font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    {link.label}
                    <ChevronDown size={18} className={`transition-transform ${mobileSubOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileSubOpen && (
                    <div className="ml-3 border-l-2 border-[#C9A227]/40 pl-3 flex flex-col gap-1 mt-1">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`px-3 py-2.5 rounded-md text-sm font-medium ${
                            isActive(child.path) ? "bg-[#1B2A4A] text-white" : "text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-md text-base font-semibold ${
                    isActive(link.path) ? "bg-[#1B2A4A] text-white" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
            <a
              href={`tel:${company.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 px-4 py-3 text-base font-semibold text-[#1B2A4A]"
            >
              <Phone size={18} className="text-[#C9A227]" /> {company.phone}
            </a>
            <button
              onClick={() => navigate("/contact")}
              className="mt-1 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-5 py-3 rounded-md transition-colors"
            >
              Demander un devis gratuit
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
